import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';
import {
  convertUnixTimestampInMilliSecondsToDateObject,
} from '@avtechsoftware/ra-javascript-utilities/src/utilities/TimeUtility';
import envConfig from '../../../envConfig';
import { CrudType } from '../../../utility/types/CrudType';
import { CrudStatus } from '../../../utility/types/CrudStatus';
import RaaAuthenticatedRemoteService from '../../../utility/RaaAuthenticatedRemoteService';

const selectMetricGroupCrudStatus = (state) => state.metricGroups.crudStatus;

const selectAllMetricsGroups = createSelector([
  (state) => state.metricGroups.metricGroupList],
(metricGroupList) => metricGroupList.map(
  (metricGroup) => ({
    ...metricGroup,
    startTime: convertUnixTimestampInMilliSecondsToDateObject(metricGroup.startTime),
    endTime: convertUnixTimestampInMilliSecondsToDateObject(metricGroup.endTime),
  }),
));

const selectMetricGroupById = (state, metricGroupId) => {
  const result = state.metricGroups.metricGroupList.find((group) => group.id === metricGroupId);
  if (!result) {
    return null;
  }
  return {
    ...result,
    startTime: convertUnixTimestampInMilliSecondsToDateObject(result.startTime),
    endTime: convertUnixTimestampInMilliSecondsToDateObject(result.endTime),
  };
};

const fetchMetricGroups = createAsyncThunk('securedApi/metricGroups/fetchAll', async (thunkAPI) => {
  try {
    const response = await RaaAuthenticatedRemoteService.globalInstance().get(`${envConfig.apiUrl}/api/v1/sensor-metrics`);
    return response.metricGroupList;
  } catch (error) {
    return thunkAPI.rejectWithValue({
      name: error.name,
      message: error.message,
      data: error.data,
    });
  }
});

const createMetricGroup = createAsyncThunk('securedApi/metricGroups/create', async (metricGroup, thunkAPI) => {
  try {
    const response = await RaaAuthenticatedRemoteService.globalInstance().post(
      `${envConfig.apiUrl}/api/v1/sensor-metrics`, {
        data: metricGroup,
      },
    );
    return response.metricGroup;
  } catch (error) {
    return thunkAPI.rejectWithValue({
      name: error.name,
      message: error.message,
      data: error.data,
    });
  }
});

const deleteMetricGroup = createAsyncThunk('securedApi/metricGroups/delete', async (metricGroupId, thunkAPI) => {
  try {
    await RaaAuthenticatedRemoteService.globalInstance().delete(`${envConfig.apiUrl}/api/v1/sensor-metrics/${metricGroupId}`);
    return thunkAPI.fulfillWithValue(metricGroupId);
  } catch (error) {
    return thunkAPI.rejectWithValue({
      name: error.name,
      message: error.message,
      data: error.data,
    });
  }
});

const updateMetricGroup = createAsyncThunk('securedApi/metricGroups/update', async (metricGroup, thunkAPI) => {
  try {
    const response = await RaaAuthenticatedRemoteService.globalInstance().patch(`${envConfig.apiUrl}/api/v1/sensor-metrics/${metricGroup.id}`, {
      data: {
        ...metricGroup,
        patchFields: [
          'endTime',
          'metricGroupChannelEntityList',
          'reportOverlayList',
          'metricTimeRangeType',
          'name',
          'startTime',
        ],
      },
    });
    return response.metricGroup;
  } catch (error) {
    return thunkAPI.rejectWithValue({
      name: error.name,
      message: error.message,
      data: error.data,
    });
  }
});

const toggleFavoriteMetricGroup = createAsyncThunk('securedApi/metricGroups/favorite', async (metricGroupId, thunkAPI) => {
  try {
    const formData = new FormData();
    formData.append('reportId', metricGroupId);
    await RaaAuthenticatedRemoteService.globalInstance().post(`${envConfig.raaUrl}/reports/data/toggle-report-favorite-status`,
      {
        data: formData,
      });
    return thunkAPI.fulfillWithValue(metricGroupId);
  } catch (error) {
    return thunkAPI.rejectWithValue(metricGroupId);
  }
});

const initialState = {
  loading: true,
  metricGroupList: [],
  loadingError: '',
  crudOperation: CrudType.READ,
  crudStatus: CrudStatus.IDLE,
  errors: [],
};

const jwtSlice = createSlice({
  name: 'metricGroups',
  initialState,
  reducers: {
    clearMetricsGroupSlice: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMetricGroups.pending, () => {
      })
      .addCase(fetchMetricGroups.fulfilled, (state, action) => {
        state.metricGroupList = action.payload;
        state.loading = false;
      })
      .addCase(fetchMetricGroups.rejected, (state) => {
        state.loading = false;
        state.loadingError = true;
      })
      .addCase(createMetricGroup.pending, (state) => {
        state.errors = [];
        state.crudOperation = CrudType.CREATE;
        state.crudStatus = CrudStatus.PENDING;
      })
      .addCase(createMetricGroup.fulfilled, (state, action) => {
        const newMetricGroup = action.payload;
        state.crudStatus = CrudStatus.SUCCESS;
        state.metricGroupList.push(newMetricGroup);
      })
      .addCase(createMetricGroup.rejected, (state, action) => {
        state.crudStatus = CrudStatus.ERROR;
        state.errors = action.payload;
      })
      .addCase(updateMetricGroup.pending, (state) => {
        state.errors = [];
        state.crudOperation = CrudType.UPDATE;
        state.crudStatus = CrudStatus.PENDING;
      })
      .addCase(updateMetricGroup.fulfilled, (state, action) => {
        state.crudStatus = CrudStatus.SUCCESS;
        const metricGroup = action.payload;
        const updatedMetricGroups = state.metricGroupList
          .filter((group) => group.id !== metricGroup.id);
        const targetMetricGroup = [...state.metricGroupList]
          .find((group) => group.id === metricGroup.id);
        const updatedMetricGroup = { ...targetMetricGroup, ...metricGroup };
        updatedMetricGroups.push(updatedMetricGroup);
        state.metricGroupList = [...updatedMetricGroups];
      })
      .addCase(updateMetricGroup.rejected, (state, action) => {
        state.crudStatus = CrudStatus.ERROR;
        state.errors = action.payload;
      })
      .addCase(deleteMetricGroup.pending, (state) => {
        state.errors = [];
        state.crudOperation = CrudType.DELETE;
        state.crudStatus = CrudStatus.PENDING;
      })
      .addCase(deleteMetricGroup.fulfilled, (state, action) => {
        state.crudStatus = CrudStatus.SUCCESS;
        const metricGroupId = action.payload;
        state.metricGroupList = state
          .metricGroupList.filter((group) => group.id !== metricGroupId);
      })
      .addCase(deleteMetricGroup.rejected, (state, action) => {
        state.crudStatus = CrudStatus.ERROR;
        state.errors = action.payload;
      })
      .addCase(toggleFavoriteMetricGroup.pending, (state, action) => {
        const metricGroupId = action.meta.arg;
        const targetGroup = state.metricGroupList
          .find((mGroup) => mGroup.id === metricGroupId);
        targetGroup.favorite = !targetGroup.favorite;
        const updatedList = state.metricGroupList.filter((group) => group.id !== metricGroupId);
        updatedList.push(targetGroup);
        state.metricGroupList = updatedList;
        state.errors = [];
        state.crudOperation = CrudType.UPDATE;
        state.crudStatus = CrudStatus.PENDING;
      })
      .addCase(toggleFavoriteMetricGroup.fulfilled, (state) => {
        state.crudStatus = CrudStatus.SUCCESS;
      })
      .addCase(toggleFavoriteMetricGroup.rejected, (state, action) => {
        state.crudStatus = CrudStatus.ERROR;
        const metricGroupId = action.payload;
        const revertedMetricGroup = state.metricGroupList
          .find((mGroup) => mGroup.id === metricGroupId);
        revertedMetricGroup.favorite = !revertedMetricGroup.favorite;
        const newGroupList = state.metricGroupList.filter((group) => group.id !== metricGroupId);
        newGroupList.push(revertedMetricGroup);
        state.metricGroupList = newGroupList;
      });
  },
});

// Ignoring the code coverage below, these are just wrapper functions that unwrap the promise
/* istanbul ignore next */
const getMetricsGroupContextRedux = (dispatch) => ({
  save: (metricGroup) => dispatch(
    createMetricGroup(
      metricGroup,
    ),
  )
    .unwrap(),
  patch: (metricGroup) => dispatch(
    updateMetricGroup(
      metricGroup,
    ),
  )
    .unwrap(),
  delete: (metricGroupId) => dispatch(
    deleteMetricGroup(
      metricGroupId,
    ),
  )
    .unwrap(),
  toggleMetricGroupFavorite: (metricGroupId) => dispatch(
    toggleFavoriteMetricGroup(
      metricGroupId,
    ),
  )
    .unwrap(),
  refresh: () => dispatch(
    fetchMetricGroups(),
  )
    .unwrap(),
});

const { actions, reducer } = jwtSlice;

export const { clearMetricsGroupSlice } = actions;
export {
  selectAllMetricsGroups,
  createMetricGroup,
  updateMetricGroup,
  deleteMetricGroup,
  toggleFavoriteMetricGroup,
  selectMetricGroupById,
  getMetricsGroupContextRedux,
  fetchMetricGroups,
  selectMetricGroupCrudStatus,
};
export default reducer;
